import {
  AspectRatio,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  Stack,
  Table,
  Textarea,
  Typography,
} from "@mui/joy";
import { theme } from "../../../../components/Theme";
import { Dispatch, SetStateAction, useEffect } from "react";
import CatalogueHeader from "../../../Items/components/CatalogueHeader";
import { ItemService } from "../../../../services/item.service";
import {
  calculatePrice,
  getDeliveryFee,
  getDiscountFee,
  getDiscountPercentage,
  getSubtotal,
  getTaxAmount,
} from "../../../../utils/cart";
import { CatalogInventoryItemType } from "../../../../types/item";
import { formatPrice } from "../../../../utils/formatPrice";
import { useFormContext } from "react-hook-form";
import { CartDataType } from "../../../../types/cart";
import { CatalogType } from "../../../../types/catalog";
import { useFeeStore } from "./store";
import { useTranslation } from "react-i18next";
import { PaymentFlowTypeEnum } from "../../../../types/order";
import { getThumbnailUrl } from "../../../../utils/items";

type Props = {
  catalog: CatalogType;
  checkOutItems: CartDataType;
  items: CatalogInventoryItemType[];
  setItems: Dispatch<SetStateAction<CatalogInventoryItemType[]>>;
};

const OrderSummary = ({ catalog, checkOutItems, items, setItems }: Props) => {
  const { t } = useTranslation();

  const {
    paymentFee,
    deliveryFee,
    taxFee,
    subtotal,
    subtotalMultiPayment,
    discount,
    setDeliveryFee,
    setDiscount,
    setTaxFee,
    setSubtotal,
    setSubtotalMultiPayment,
    setDiscountPercentage,
  } = useFeeStore();

  const {
    register,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    const deliveryFee = getDeliveryFee(catalog, checkOutItems, subtotal);
    setDeliveryFee(deliveryFee);

    const discountFee = getDiscountFee(catalog, subtotal + deliveryFee);
    setDiscount(discountFee);

    const discountPercentage = getDiscountPercentage(
      catalog,
      subtotal + deliveryFee
    );
    setDiscountPercentage(discountPercentage);

    const taxFee =
      getTaxAmount(
        checkOutItems.items,
        items,
        catalog,
        deliveryFee - discountFee
      ) ?? 0;
    setTaxFee(taxFee);
  }, [items]);

  /**
   * get CatalogInventoryItems of the checkOutItems
   */
  useEffect(() => {
    if (checkOutItems && catalog) {
      const ids = checkOutItems?.items.map((i) => i.cid) || [];
      if (checkOutItems.items.length > 0) {
        ItemService.getCatalogInventoryItemsByIds({ ids })
          .then((res) => {
            setItems(res);
            getSubtotal({
              selected: res.map((i) => i.inventoryItem.id),
              items: res,
              cartItems: checkOutItems,
              callback: (subtotal) => {
                if (
                  catalog.paymentFlowType ===
                    PaymentFlowTypeEnum.MULTIPLE_PAYMENTS &&
                  catalog.multiPaymentsData
                ) {
                  const firstPayment =
                    catalog.multiPaymentsData.paymentHistory.find(
                      (e) => e.time === 1
                    );
                  if (firstPayment) {
                    const subTotalMultiPayment =
                      ((parseFloat(subtotal.toString()) +
                        parseFloat(taxFee.toString()) +
                        parseFloat(deliveryFee.toString()) -
                        parseFloat(discount.toString())) *
                        parseFloat(firstPayment.percentage.toString())) /
                      100;
                    setSubtotalMultiPayment(subTotalMultiPayment);
                  }
                }
                setSubtotal(subtotal);
              },
            });
          })
          .catch((err) => console.error(err))
          .finally(() => {
            // setIsLoading(false);
          });
      } else {
        setItems([]);
        // setIsLoading(false);
      }
    }
  }, [checkOutItems, catalog, taxFee, deliveryFee, discount]);

  console.log("CHECKOUT ITEMS", checkOutItems);

  return (
    <Stack
      direction={"column"}
      sx={{
        gap: "16px",
      }}
    >
      <Typography level="title-md" textColor={theme.palette.primary[500]}>
        {t("catalog.checkout.orderSummary.title")}
      </Typography>

      {/* order items table lg */}
      <Table
        borderAxis="none"
        sx={{
          "& thead th": {
            backgroundColor: theme.palette.neutral[50],
            p: "16px",
            verticalAlign: "middle",
            textAlign: "center",
          },
          "& thead th:nth-child(1)": {
            width: { xs: "100%", md: "50%" },
            textAlign: "left",
          },
          // hide column 2 and 3 in mobile
          "& thead th:nth-child(2)": {
            display: { xs: "none", md: "table-cell" },
          },
          "& thead th:nth-child(3)": {
            display: { xs: "none", md: "table-cell" },
          },
          "& tbody td": {
            py: "16px",
            verticalAlign: "middle",
            textAlign: "center",
            borderBottom: `1px solid ${theme.palette.others.list_item_border}`,
          },
          // hide column 1 and 3 in mobile
          "& tbody td:nth-child(1)": {
            display: { xs: "none", md: "table-cell" },
          },
          "& tbody td:nth-child(3)": {
            display: { xs: "none", md: "table-cell" },
          },
        }}
      >
        <colgroup>
          {/* Fixed width for the first column */}
          <col style={{ width: "16px" }} />
          {/* Second column takes remaining space */}
          <col style={{ width: "40%" }} />
        </colgroup>
        <thead>
          <tr>
            <th colSpan={2}>
              <CatalogueHeader
                catalog={catalog}
                size="sm"
                withCatalogType={false}
              />
            </th>
            <th>
              <Typography level="text-sm-regular">
                {t("catalog.checkout.orderSummary.unitPrice")}
              </Typography>
            </th>
            <th>
              <Typography level="text-sm-regular">
                {t("catalog.checkout.orderSummary.subtotal")}
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {checkOutItems?.items?.map((item) => {
            const catalogInventoryItem = items.find((i) => i.id === item.cid);
            console.log("CATALOG INVENTORY ITEM", {
              catalog,
              catalogInventoryItem,
            });

            return (
              <tr key={item.cid}>
                {catalogInventoryItem && (
                  <>
                    <td style={{ width: "32px !important", border: "none" }} />
                    <td>
                      <Stack
                        direction={"row"}
                        sx={{
                          width: "100%",
                          gap: "16px",
                          alignItems: "center",
                          textAlign: "left",
                        }}
                      >
                        {/* item image */}
                        <AspectRatio
                          ratio={1}
                          variant="plain"
                          sx={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "3.71px",
                          }}
                        >
                          <img
                            alt={catalogInventoryItem.inventoryItem.sku}
                            src={getThumbnailUrl(
                              catalogInventoryItem.inventoryItem
                            )}
                            width={"100%"}
                            height={"100%"}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "/inventory-form-no-img.png";
                            }}
                            // className="lg:border border-gray-300 rounded-t-lg lg:rounded-lg w-[64px] h-[64px] lg:w-[100px] lg:h-[100px] object-contain flex-shrink-0"
                          />
                        </AspectRatio>

                        {/* item details */}
                        <Stack direction={"column"} gap={"4px"}>
                          <Typography level="text-xs-regular">
                            {catalogInventoryItem?.inventoryItem.sku}
                          </Typography>
                          <Typography level="text-sm-bold">
                            {catalogInventoryItem?.inventoryItem.productName}
                          </Typography>
                          <Typography level="text-xs-regular">
                            {`x${item.quantity} ${
                              catalogInventoryItem?.inventoryItem[item.uom]
                            }`}
                          </Typography>
                        </Stack>
                      </Stack>
                    </td>
                    {/* unit price */}
                    <td>
                      <Typography level="text-sm-medium">
                        {catalog.currency}{" "}
                        {formatPrice(
                          calculatePrice(catalogInventoryItem, item.uom),
                          catalog.currency
                        )}
                      </Typography>
                    </td>
                    {/* subtotal */}
                    <td>
                      <Typography level="text-md-bold">
                        {catalog.currency}{" "}
                        {formatPrice(
                          calculatePrice(catalogInventoryItem, item.uom) *
                            item.quantity,
                          catalog.currency
                        )}
                      </Typography>
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
      {/* fees */}
      <Grid
        container
        columns={2}
        sx={{
          width: "100%",
          px: "32px",
          py: "8px",
          rowGap: "8px",
        }}
      >
        {/* delivery fee */}
        <Grid xs={1}>
          <Typography level={"text-sm-medium"}>
            {t("catalog.checkout.orderSummary.fees.delivery")}
          </Typography>
        </Grid>
        <Grid xs={1} sx={{ textAlign: "right" }}>
          <Typography
            level={"text-sm-semibold"}
            textColor={theme.palette.common.black}
          >
            {catalog.currency} {formatPrice(deliveryFee, catalog.currency)}
          </Typography>
        </Grid>

        {/* platform fee */}
        <Grid xs={1}>
          <Typography level={"text-sm-medium"}>
            {t("catalog.checkout.orderSummary.fees.payment")}
          </Typography>
        </Grid>
        <Grid xs={1} sx={{ textAlign: "right" }}>
          <Typography
            level={"text-sm-semibold"}
            textColor={theme.palette.common.black}
          >
            {catalog.currency} {formatPrice(paymentFee, catalog.currency)}
          </Typography>
        </Grid>

        {/* additional discount */}
        <Grid xs={1}>
          <Typography level={"text-sm-medium"}>
            {t("catalog.checkout.orderSummary.fees.discount")}
          </Typography>
        </Grid>
        <Grid xs={1} sx={{ textAlign: "right" }}>
          <Typography
            level={"text-sm-semibold"}
            textColor={theme.palette.common.black}
          >
            {`(${catalog.currency} ${formatPrice(discount, catalog.currency)})`}
          </Typography>
        </Grid>

        {/* subtotal */}
        <Grid xs={1}>
          <Typography level={"text-sm-medium"}>
            {t("catalog.checkout.orderSummary.subtotal")}
          </Typography>
        </Grid>
        <Grid xs={1} sx={{ textAlign: "right" }}>
          <Typography
            level={"text-sm-semibold"}
            textColor={theme.palette.common.black}
          >
            {catalog.currency}{" "}
            {formatPrice(
              subtotal + deliveryFee + paymentFee - discount,
              catalog.currency
            )}
          </Typography>
        </Grid>

        {/* taxes */}
        <Grid xs={1}>
          <Typography level={"text-sm-medium"}>
            {t("catalog.checkout.orderSummary.fees.taxes")}
          </Typography>
        </Grid>
        <Grid xs={1} sx={{ textAlign: "right" }}>
          <Typography
            level={"text-sm-semibold"}
            textColor={theme.palette.common.black}
          >
            {catalog.currency} {formatPrice(taxFee, catalog.currency)}
          </Typography>
        </Grid>
      </Grid>

      {/* remarks */}
      <FormControl
        error={!!errors.remarks?.message}
        sx={{
          backgroundColor: theme.palette.neutral[50],
          py: "16px",
          px: "32px",
        }}
      >
        <FormLabel>
          <Typography level={"text-sm-regular"}>
            {t("catalog.checkout.orderSummary.remarksLabel")}
          </Typography>
        </FormLabel>

        <Textarea
          {...register("remarks")}
          placeholder={t("catalog.checkout.orderSummary.remarksPlaceholder")}
          minRows={2}
        />

        {errors.remarks?.message && (
          <FormHelperText>{errors.remarks?.message as string}</FormHelperText>
        )}
      </FormControl>

      {/* multipayment */}
      {catalog.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS && (
        <>
          <Stack
            direction={"row"}
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography level={"text-sm-medium"}>Grand Total</Typography>
            <Typography level={"text-sm-medium"} textColor={"neutral.500"}>
              {catalog.currency}{" "}
              {formatPrice(
                subtotal + deliveryFee + taxFee + paymentFee - discount,
                catalog.currency
              )}
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography level={"text-sm-medium"}>
              {t("catalog.checkout.paymentPlan")}
            </Typography>
            <Typography level={"text-sm-medium"} textColor={"neutral.500"}>
              {catalog.multiPaymentsData?.paymentTimes}{" "}
              {t("catalog.checkout.payments")}
            </Typography>
          </Stack>
          <Stack
            direction={"row"}
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography level={"text-sm-medium"}>
              {t("catalog.checkout.payment1st")}
            </Typography>
            <Typography level={"text-sm-medium"} textColor={"neutral.500"}>
              {catalog.multiPaymentsData?.paymentHistory[0].percentage}
              {" %"}
            </Typography>
          </Stack>
        </>
      )}

      {/* payable amount */}
      <Stack
        direction={"row"}
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Typography level={"text-md-bold"}>
          {t("catalog.checkout.orderSummary.payableAmount")}
        </Typography>
        <Typography
          level={"text-md-bold"}
          textColor={theme.palette.primary[500]}
        >
          {catalog.currency}{" "}
          {formatPrice(
            (catalog.paymentFlowType === PaymentFlowTypeEnum.MULTIPLE_PAYMENTS
              ? subtotalMultiPayment
              : subtotal + deliveryFee + taxFee - discount) + paymentFee,
            catalog.currency
          )}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default OrderSummary;
